import type { ReportRepository } from '../../application/ReportRepository'
import {
  DATASETS,
  FINANCIAL_REPORTS,
  ReportCommandError,
  validBackupReason,
  type DatasetKey,
  type ReportJob,
} from '../../domain/report'

/**
 * Dépôt en mémoire (mode fixtures et tests). Les comptes sont des exemples, jamais des faits : aucune décision ne
 * se prend en mode fixtures. Une génération avance de moitié à chaque lecture, puis se termine : la progression se
 * teste sans minuterie.
 */
const SAMPLE_COUNTS: Record<DatasetKey, number> = {
  clients: 456,
  client_addresses: 470,
  client_contacts: 112,
  contracts_residential: 431,
  contracts_commercial: 8,
  contracts_multifamily: 1,
  contracts_vip: 67,
  invoices: 613,
  payments: 4,
  taxes: 330,
  routes: 18,
  route_addresses: 452,
  missions: 41,
  operations: 3,
  employees: 27,
  equipments: 19,
  leads: 143,
  quotes: 12,
  sms_consents: 806,
  incidents: 6,
}

const FIXTURE_USER = { userId: '00000000-0000-4000-a000-0000000000aa', name: 'Admin RÉCA' }

function baseJob(overrides: Partial<ReportJob> & Pick<ReportJob, 'jobId' | 'kind' | 'category'>): ReportJob {
  return {
    parameters: {},
    periodStart: null,
    periodEnd: null,
    reason: null,
    status: 'queued',
    progressPercent: 0,
    currentStep: null,
    requestedBy: FIXTURE_USER,
    createdAt: new Date().toISOString(),
    startedAt: null,
    completedAt: null,
    expiresAt: null,
    snapshotAt: null,
    fileName: null,
    sizeBytes: null,
    rowCounts: null,
    errorCode: null,
    errorMessage: null,
    deletedAt: null,
    ...overrides,
  }
}

/** Exécute `run` en promesse : une erreur levée devient un rejet. */
function settle<T>(run: () => T): Promise<T> {
  return new Promise((resolve) => {
    resolve(run())
  })
}

export function createFixtureReportRepository(options: { seed?: ReportJob[] } = {}): ReportRepository {
  const jobs: ReportJob[] = [...(options.seed ?? [])]
  const byKey = new Map<string, ReportJob>()
  let sequence = 0

  function advance(job: ReportJob): ReportJob {
    if (job.status === 'queued' || job.status === 'processing') {
      const progress = Math.min(job.progressPercent + 50, 100)
      Object.assign(job, {
        status: 'processing',
        progressPercent: progress,
        startedAt: job.startedAt ?? new Date().toISOString(),
      })
      if (progress >= 100) {
        const now = new Date()
        Object.assign(job, {
          status: 'completed',
          currentStep: null,
          completedAt: now.toISOString(),
          expiresAt: new Date(now.getTime() + 30 * 24 * 3600 * 1000).toISOString(),
          snapshotAt: now.toISOString(),
          fileName: job.kind === 'BACKUP' ? 'RECA_SAUVEGARDE_FIXTURE.zip' : 'RECA_EXPORT_FIXTURE.xlsx',
          sizeBytes: job.kind === 'BACKUP' ? 2_200_000 : 65_536,
        })
      }
    }
    return { ...job }
  }

  function find(jobId: string): ReportJob {
    const job = jobs.find((candidate) => candidate.jobId === jobId)
    if (!job) throw new ReportCommandError('REPORT_JOB_NOT_FOUND')
    return job
  }

  return {
    exportPreview({ datasets }) {
      return Promise.resolve(
        DATASETS.map((dataset) => ({
          dataset: dataset.key,
          family: dataset.family,
          allowed: true,
          count: !datasets || datasets.includes(dataset.key) ? SAMPLE_COUNTS[dataset.key] : null,
          missingPermission: null,
        })),
      )
    },
    filterOptions() {
      return Promise.resolve({
        routes: [
          { value: '00000000-0000-4000-a100-000000000001', label: 'Route Nord', detail: '2026-2027' },
          { value: '00000000-0000-4000-a100-000000000002', label: 'Route Sud', detail: '2026-2027' },
        ],
        sectors: [{ value: '00000000-0000-4000-a200-000000000001', label: 'Saint-Jérôme' }],
        operators: [{ value: '00000000-0000-4000-a300-000000000001', label: 'Olivier Exemple' }],
        equipments: [{ value: '00000000-0000-4000-a400-000000000001', label: 'T-01 · Kubota' }],
        operations: [
          { value: '00000000-0000-4000-a500-000000000001', label: 'OP-2026-001', detail: '2026-11-20' },
        ],
      })
    },
    financialPreview({ report }) {
      if (!FINANCIAL_REPORTS.some((definition) => definition.key === report))
        return Promise.reject(new ReportCommandError('REPORT_UNKNOWN'))
      return Promise.resolve({
        report,
        summary: [{ key: 'total', label: 'Total (exemple)', amount: 1234.5, detail: 'données d’exemple' }],
        sheet: 'by_month',
        rowCount: 0,
        rows: [],
      })
    },
    begin(command, idempotencyKey) {
      const existing = byKey.get(idempotencyKey)
      if (existing) return Promise.resolve({ ...existing })
      if (command.kind === 'BACKUP' && !validBackupReason(command.reason))
        return Promise.reject(new ReportCommandError('REPORT_REASON_REQUIRED'))
      if (
        command.kind === 'BACKUP' &&
        jobs.some(
          (job) =>
            job.kind === 'BACKUP' &&
            job.status !== 'completed' &&
            job.status !== 'failed' &&
            job.status !== 'expired',
        )
      )
        return Promise.reject(new ReportCommandError('REPORT_BACKUP_IN_PROGRESS'))
      sequence++
      const job = baseJob({
        jobId: `00000000-0000-4000-a000-${String(sequence).padStart(12, '0')}`,
        kind: command.kind,
        category:
          command.kind === 'EXPORT' ? 'MIXED' : command.kind === 'FINANCIAL_REPORT' ? command.report : 'FULL',
        parameters:
          command.kind === 'EXPORT'
            ? { datasets: command.datasets, periodKey: command.period.key }
            : command.kind === 'FINANCIAL_REPORT'
              ? { periodKey: command.period.key, ...(command.axis ? { axis: command.axis } : {}) }
              : {},
        periodStart: command.kind === 'BACKUP' ? null : command.period.start,
        periodEnd: command.kind === 'BACKUP' ? null : command.period.end,
        reason: command.kind === 'BACKUP' ? command.reason.trim() : null,
      })
      jobs.unshift(job)
      byKey.set(idempotencyKey, job)
      return Promise.resolve({ ...job })
    },
    jobs(query = {}) {
      return Promise.resolve(
        jobs
          .filter((job) => !query.kind || job.kind === query.kind)
          .filter((job) => !query.before || job.createdAt < query.before)
          .slice(0, query.limit ?? 50)
          .map(advance),
      )
    },
    job(jobId) {
      return settle(() => advance(find(jobId)))
    },
    download(jobId) {
      return settle(() => {
        const job = find(jobId)
        if (job.status === 'expired') throw new ReportCommandError('REPORT_FILE_EXPIRED')
        if (job.status !== 'completed') throw new ReportCommandError('REPORT_FILE_NOT_READY')
        return { url: `about:blank#${job.fileName ?? ''}`, fileName: job.fileName ?? '' }
      })
    },
    deleteFile(jobId) {
      return settle(() => {
        const job = find(jobId)
        if (job.status !== 'completed') throw new ReportCommandError('REPORT_JOB_NOT_DELETABLE')
        Object.assign(job, { status: 'expired', deletedAt: new Date().toISOString() })
        return { ...job }
      })
    },
  }
}
